import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert, RefreshCw, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { SUCCESS_METRICS, OBJECTIONS } from "../data/questions";
import { getLead, setLeadStatus, clearReview, saveDiscovery, saveAnr, syncLead, money, errText } from "../lib/api";

const SEVERITY = { block: "text-red-400 border-red-500/40 bg-red-500/10", warn: "text-brand-pink border-brand-pink/40 bg-brand-pink/10", info: "text-brand-indigo border-brand-indigo/40 bg-brand-indigo/10" };
const inputCls = "h-11 rounded-sm border-zinc-800 bg-zinc-900/70 px-4 text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-brand-pink";
const taCls = "rounded-sm border-zinc-800 bg-zinc-900/70 px-4 py-3 text-sm leading-relaxed text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-brand-pink";

const DISCOVERY_QUESTIONS = [
  ["confirm_90_day_goal", "Confirm primary goal", "“Is that still the #1 outcome?”"],
  ["why_now", "Why now", "“What changes if nothing happens in six months?”"],
  ["time_reality", "Time reality", "“How many hours a week can you commit?”"],
  ["control_ownership", "Control", "“What do you want to own vs what should Sabrea own?”"],
  ["budget_boundary", "Constraints", "“What's the real budget boundary — responsible, not aspirational?”"],
  ["past_hires_failure", "Past hires", "“What caused past help to fail?”"],
  ["approvals", "Approvals", "“Who approves releases, marketing and spend?”"],
  ["rights_blockers", "Rights blockers", "“Any splits, samples or ownership uncertainties?”"],
];

const ANR_FIELDS = [
  ["sonic_description", "Sonic description (2–4 sentences)"],
  ["distinctive_observation", "One distinctive observation (specific)"],
  ["comparable_ecosystem", "Comparable ecosystem (audience fit, not “you sound like”)"],
  ["mix_observation", "One mix/production observation (objective)"],
  ["repertoire_question", "One repertoire question (ask, don't declare)"],
  ["catalog_opportunity", "One catalog opportunity"],
  ["brand_fan_observation", "One brand/fan observation"],
  ["business_admin_issue", "One business/admin issue"],
  ["follow_up_question", "One follow-up question"],
];

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => getLead(id).then(setLead);
  useEffect(() => { load(); }, [id]);

  if (!lead) return <p className="p-10 font-mono-tech text-[11px] text-zinc-500">Loading…</p>;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await load(); toast.success(msg); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const doSync = async () => {
    setBusy(true);
    try {
      const r = await syncLead(id);
      await load();
      r.ok ? toast.success("Synced to Notion.") : toast.error("Sync completed with errors — see the sync log.");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const a = lead.answers || {};

  return (
    <div className="p-6 md:p-10">
      <Link to="/control" data-testid="back-to-leads" className="inline-flex items-center gap-2 font-mono-tech text-[10px] text-zinc-500 hover:text-brand-pink transition-colors duration-200">
        <ArrowLeft className="h-3 w-3" /> All leads
      </Link>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono-tech text-[10px] text-zinc-500">{lead.type} · {a.location} · submitted {lead.created_at?.slice(0, 10)}</p>
          <h1 className="mt-2 font-display text-4xl font-black text-zinc-50" data-testid="lead-name">{lead.artist_name}</h1>
          <p className="mt-2 text-sm text-zinc-400">{a.full_name} · {a.email} · {a.phone}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["A&R Reviewed", "Discovery Booked", "Qualified", "Closed Won", "Closed Lost"].map((s) => (
            <Button key={s} data-testid={`status-${s}`} disabled={busy} onClick={() => act(() => setLeadStatus(id, s), `Status set to ${s}.`)}
              className={`h-10 rounded-full border px-5 font-mono-tech text-[10px] transition-colors duration-200 ${lead.status === s ? "border-brand-pink bg-brand-pink text-zinc-950" : "border-zinc-800 bg-transparent text-zinc-300 hover:border-brand-pink hover:text-zinc-50"}`}>
              {s}
            </Button>
          ))}
        </div>
      </div>

      {lead.flags?.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2" data-testid="lead-flags">
          {lead.flags.map((f) => (
            <span key={f.code} className={`rounded-sm border px-3 py-1.5 font-mono-tech text-[10px] ${SEVERITY[f.severity]}`}>{f.label}</span>
          ))}
        </div>
      )}

      {lead.human_review_required && (
        <div className="mt-6 border border-red-500/40 bg-red-500/5 p-6" data-testid="review-panel">
          <p className="flex items-center gap-2 font-mono-tech text-[11px] text-red-400"><ShieldAlert className="h-4 w-4" /> Human review required{lead.review_cleared ? " — cleared" : ""}</p>
          <ul className="mt-4 space-y-2 text-sm text-zinc-300">
            {lead.reasons.map((r, i) => <li key={i}>— {r}</li>)}
          </ul>
          {lead.required_questions?.length > 0 && (
            <>
              <p className="mt-5 font-mono-tech text-[10px] text-zinc-500">Required before scoping</p>
              <ul className="mt-2 space-y-1.5 text-sm text-zinc-400">
                {lead.required_questions.map((q, i) => <li key={i}>{i + 1}. {q}</li>)}
              </ul>
            </>
          )}
          {!lead.review_cleared && (
            <Button data-testid="clear-review-btn" disabled={busy} onClick={() => act(() => clearReview(id), "Review cleared.")}
              className="mt-6 h-10 rounded-full bg-zinc-100 px-6 font-mono-tech text-[10px] text-zinc-950 hover:bg-brand-pink transition-colors duration-200">
              <Check className="mr-2 h-3 w-3" /> Mark review cleared
            </Button>
          )}
          {lead.quoting_blocked && <p className="mt-4 font-mono-tech text-[10px] text-red-400">Quoting is hard-blocked for this lead.</p>}
        </div>
      )}

      <Tabs defaultValue="plan" className="mt-10">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 border-b border-zinc-900 bg-transparent p-0">
          {[["plan", "NOW / NEXT / LATER"], ["anr", "A&R Prep Card"], ["discovery", "Discovery Script"], ["answers", "Raw Answers"], ["sync", "Notion Sync"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="rounded-none border-b-2 border-transparent bg-transparent px-3 pb-3 font-mono-tech text-[10px] text-zinc-500 data-[state=active]:border-brand-pink data-[state=active]:bg-transparent data-[state=active]:text-brand-pink">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="plan" className="mt-8">
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <section className="mb-8" data-testid="applicant-summary">
                <h3 className="font-mono-tech text-xs text-brand-pink">Applicant summary</h3>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <SummaryList label="Looking for" items={lead.summary?.interests} />
                  <SummaryList label="Wants to accomplish next" items={lead.summary?.goals} />
                  <SummaryList label="Review focus" items={lead.review_focus} />
                  <SummaryList label="Active branches" items={Object.entries(lead.branches || {}).filter(([, v]) => v).map(([k]) => k)} />
                </div>
              </section>
              {["now", "next", "later"].map((p) => (
                <section key={p} className="mb-8" data-testid={`plan-${p}`}>
                  <h3 className="font-mono-tech text-xs text-brand-indigo">{p}</h3>
                  <div className="mt-3 border-t border-zinc-900">
                    {lead.plan[p].length === 0 && <p className="py-4 text-sm text-zinc-600">Empty.</p>}
                    {lead.plan[p].map((i) => (
                      <div key={i.sku} className="flex items-start justify-between gap-6 border-b border-zinc-900 py-4">
                        <div>
                          <p className="font-display font-bold text-zinc-50">{i.name} <span className="ml-2 font-mono-tech text-[9px] text-zinc-600">{i.sku}</span></p>
                          <p className="mt-1 text-sm text-zinc-500">{i.description}</p>
                        </div>
                        <p className="whitespace-nowrap font-mono-tech text-[10px] text-zinc-400">{money(i.price_low)}–{money(i.price_high)}</p>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
              {lead.self_directed?.length > 0 && (
                <section data-testid="lead-self-directed">
                  <h3 className="font-mono-tech text-xs text-zinc-500">Self-directed steps</h3>
                  <ul className="mt-3 space-y-2 text-sm text-zinc-300">{lead.self_directed.map((s) => <li key={s.product_line}>— {s.step}</li>)}</ul>
                </section>
              )}
            </div>
            <aside className="lg:col-span-4">
              <div className="border border-zinc-900 bg-zinc-900/40 p-6">
                <p className="font-mono-tech text-[10px] text-zinc-500">Internal NOW range (catalog-derived)</p>
                <p className="mt-2 font-display text-2xl font-black text-zinc-50" data-testid="lead-price">{lead.price_low === null ? "Blocked" : `${money(lead.price_low)}–${money(lead.price_high)}`}</p>
                <p className="mt-3 text-xs leading-relaxed text-zinc-500">{lead.price_note}</p>
                <div className="mt-6 space-y-2 border-t border-zinc-800 pt-5 font-mono-tech text-[10px] text-zinc-400">
                  <p>Stated budget: {lead.budget_band || "—"}</p>
                  <p>Roles: {(lead.answers?.roles || []).join(", ") || "—"}</p>
                  <p>Weekly time: {lead.answers?.weekly_time || "—"}</p>
                  <p>In person: {lead.answers?.in_person || "—"}{(lead.answers?.in_person_where || []).length ? ` (${lead.answers.in_person_where.join(", ")})` : ""}</p>
                  <p>Music stage: {(lead.answers?.music_stage || []).join(", ") || "—"}</p>
                  <p>Genres: {(lead.answers?.genres || []).join(", ") || "—"}</p>
                  <p>Support style: {(lead.answers?.support_style || []).join(", ") || "—"}</p>
                  <p>Links shared: {(lead.answers?.music_links || []).length}</p>
                </div>
                {lead.booking && (
                  <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="lead-booking">
                    <p className="font-mono-tech text-[10px] text-green-400">Introduction booked</p>
                    <p className="mt-1 text-sm text-zinc-300">{lead.booking.start_time ? new Date(lead.booking.start_time).toLocaleString() : "Time pending"}</p>
                    <p className="font-mono-tech text-[10px] text-zinc-600">{lead.booking.status}</p>
                  </div>
                )}
              </div>
              {lead.ask_on_call?.length > 0 && (
                <div className="mt-4 border border-zinc-900 bg-zinc-900/40 p-6" data-testid="ask-on-call">
                  <p className="font-mono-tech text-[10px] text-brand-indigo">Confirm on the call (never asked publicly)</p>
                  <ul className="mt-3 space-y-2 text-sm text-zinc-400">
                    {lead.ask_on_call.map((q, i) => <li key={i}>— {q}</li>)}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </TabsContent>

        <TabsContent value="anr" className="mt-8"><AnrCard lead={lead} onSaved={load} /></TabsContent>
        <TabsContent value="discovery" className="mt-8"><DiscoveryScript lead={lead} onSaved={load} /></TabsContent>

        <TabsContent value="answers" className="mt-8">
          <div className="border-t border-zinc-900" data-testid="raw-answers">
            {Object.entries(a).map(([k, v]) => (
              <div key={k} className="grid gap-1 border-b border-zinc-900 py-3 md:grid-cols-[280px_1fr]">
                <p className="font-mono-tech text-[10px] text-zinc-500">{k}</p>
                <p className="text-sm text-zinc-300 break-words">{Array.isArray(v) ? v.join(", ") : String(v || "—")}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sync" className="mt-8">
          <div className="border border-zinc-900 bg-zinc-900/40 p-6" data-testid="sync-panel">
            <p className="font-mono-tech text-[10px] text-zinc-500">Notion state</p>
            <p className={`mt-2 font-display text-xl font-black ${lead.notion?.synced ? "text-green-400" : "text-zinc-400"}`} data-testid="sync-state">
              {lead.notion?.synced ? "Synced" : "Not synced"}
            </p>
            {lead.notion?.last_attempt && <p className="mt-2 font-mono-tech text-[10px] text-zinc-500">Last push {lead.notion.last_attempt.slice(0, 19).replace("T", " ")}</p>}
            {lead.notion?.last_pull && <p className="mt-1 font-mono-tech text-[10px] text-zinc-500">Last reconcile {lead.notion.last_pull.slice(0, 19).replace("T", " ")}</p>}
            {lead.notion_notes && (
              <div className="mt-4 border-l-2 border-brand-indigo pl-4" data-testid="notion-notes">
                <p className="font-mono-tech text-[10px] text-brand-indigo">Notes edited in Notion</p>
                <p className="mt-1 whitespace-pre-line text-sm text-zinc-300">{lead.notion_notes}</p>
              </div>
            )}
            {lead.notion?.last_error && <p className="mt-3 border-l-2 border-red-500 pl-4 text-sm text-red-400">{lead.notion.last_error}</p>}
            {lead.notion?.results && (
              <pre className="mt-4 overflow-x-auto border border-zinc-800 bg-black p-4 text-[11px] text-zinc-400">{JSON.stringify(lead.notion.results, null, 2)}</pre>
            )}
            <Button data-testid="sync-notion-btn" disabled={busy} onClick={doSync}
              className="mt-6 h-11 rounded-full bg-brand-pink px-7 font-semibold text-zinc-950 hover:bg-brand-pink-soft transition-colors duration-200">
              <RefreshCw className="mr-2 h-4 w-4" /> Push to Notion
            </Button>
            <Link to="/control/notion" className="ml-4 font-mono-tech text-[10px] text-brand-indigo hover:text-brand-indigo-soft transition-colors duration-200">Configure databases →</Link>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const SummaryList = ({ label, items }) => (
  <div className="border border-zinc-900 bg-zinc-900/30 p-4">
    <p className="font-mono-tech text-[10px] text-zinc-500">{label}</p>
    <ul className="mt-2 space-y-1 text-sm text-zinc-300">
      {(items || []).length ? items.map((i) => <li key={i}>— {i}</li>) : <li className="text-zinc-600">—</li>}
    </ul>
  </div>
);

const AnrCard = ({ lead, onSaved }) => {
  const [form, setForm] = useState(lead.anr || { strongest_submissions: (lead.answers?.music_links || []).slice(0, 3) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await saveAnr(lead.id, { ...form, completed: true }); await onSaved(); toast.success("A&R Prep Card saved."); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="anr-card">
      <p className="text-sm text-zinc-500">Required before the discovery call. Budget 20–40 minutes.</p>
      {[0, 1, 2].map((i) => (
        <div key={i} className="mt-5">
          <p className="font-mono-tech text-[10px] text-zinc-500">Strongest submission #{i + 1}</p>
          <Input data-testid={`anr-submission-${i}`} className={inputCls + " mt-2"} value={(form.strongest_submissions || [])[i] || ""}
            onChange={(e) => { const arr = [...(form.strongest_submissions || ["", "", ""])]; arr[i] = e.target.value; set("strongest_submissions", arr); }} />
        </div>
      ))}
      {ANR_FIELDS.map(([k, label]) => (
        <div key={k} className="mt-6">
          <p className="text-sm font-semibold text-zinc-200">{label}</p>
          <Textarea data-testid={`anr-${k}`} rows={2} className={taCls + " mt-2"} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
        </div>
      ))}
      <div className="mt-8 border border-red-500/30 bg-red-500/5 p-5">
        <p className="font-mono-tech text-[10px] text-red-400">Internal only — never shown to the customer</p>
        <Textarea data-testid="anr-internal_label_interest" rows={2} className={taCls + " mt-3"} placeholder="Label interest notes"
          value={form.internal_label_interest || ""} onChange={(e) => set("internal_label_interest", e.target.value)} />
      </div>
      <Button data-testid="save-anr-btn" disabled={busy} onClick={save}
        className="mt-8 h-11 rounded-full bg-brand-pink px-7 font-semibold text-zinc-950 hover:bg-brand-pink-soft transition-colors duration-200">Save prep card</Button>
    </div>
  );
};

const DiscoveryScript = ({ lead, onSaved }) => {
  const [form, setForm] = useState(lead.discovery || { success_metrics: [], objections: [], recommended_product_lines: [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => { const list = form[k] || []; set(k, list.includes(v) ? list.filter((x) => x !== v) : [...list, v]); };

  const save = async () => {
    setBusy(true);
    try { await saveDiscovery(lead.id, form); await onSaved(); toast.success("Discovery call captured."); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const anrRequired = !lead.anr?.completed;

  return (
    <div className="max-w-3xl" data-testid="discovery-script">
      <p className="text-sm text-zinc-500">Internal script · 45–55 minutes. Never promise results, streams or editorial consideration.</p>
      {anrRequired && <p className="mt-3 border-l-2 border-brand-pink pl-4 font-mono-tech text-[10px] text-brand-pink">A&R Prep Card is not complete yet — required before this call.</p>}

      <div className="mt-8">
        <p className="text-sm font-semibold text-zinc-200">Opening (credibility)</p>
        <p className="mt-1 text-sm text-zinc-500">“Was <em>[specific moment in track]</em> intentional?” — fill from A&R prep.</p>
        <Input data-testid="discovery-opening_observation" className={inputCls + " mt-3"} value={form.opening_observation || ""} onChange={(e) => set("opening_observation", e.target.value)} />
      </div>

      {DISCOVERY_QUESTIONS.map(([k, title, prompt], i) => (
        <div key={k} className="mt-7 border-t border-zinc-900 pt-5">
          <p className="font-mono-tech text-[10px] text-brand-indigo">Q{i + 1} · {title}</p>
          <p className="mt-1 text-sm text-zinc-400">{prompt}</p>
          <Textarea data-testid={`discovery-${k}`} rows={2} className={taCls + " mt-3"} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
        </div>
      ))}

      <div className="mt-7 border-t border-zinc-900 pt-5">
        <p className="font-mono-tech text-[10px] text-brand-indigo">Q9 · Success metrics (choose 3–5)</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {SUCCESS_METRICS.map((m) => (
            <button key={m} data-testid={`metric-${m}`} onClick={() => toggle("success_metrics", m)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${(form.success_metrics || []).includes(m) ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-300 hover:border-brand-indigo/60"}`}>{m}</button>
          ))}
        </div>
        <p className="mt-2 font-mono-tech text-[10px] text-zinc-600">{(form.success_metrics || []).length} selected</p>
      </div>

      <h3 className="mt-12 font-display text-xl font-black text-zinc-50">Call output</h3>
      {[["issue_now", "Top issue — NOW"], ["issue_next", "Top issue — NEXT"], ["issue_later", "Top issue — LATER"]].map(([k, l]) => (
        <div key={k} className="mt-5">
          <p className="text-sm font-semibold text-zinc-200">{l}</p>
          <Input data-testid={`discovery-${k}`} className={inputCls + " mt-2"} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
        </div>
      ))}

      <div className="mt-7">
        <p className="text-sm font-semibold text-zinc-200">Objections</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {OBJECTIONS.map((o) => (
            <button key={o} data-testid={`objection-${o}`} onClick={() => toggle("objections", o)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${(form.objections || []).includes(o) ? "border-brand-pink bg-brand-pink text-zinc-950" : "border-zinc-800 text-zinc-300 hover:border-brand-pink/60"}`}>{o}</button>
          ))}
        </div>
        <Textarea data-testid="discovery-objection_notes" rows={2} className={taCls + " mt-3"} placeholder="Objection notes" value={form.objection_notes || ""} onChange={(e) => set("objection_notes", e.target.value)} />
      </div>

      <div className="mt-7">
        <p className="text-sm font-semibold text-zinc-200">Recommendation (product lines from the routed plan)</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[...lead.plan.now, ...lead.plan.next, ...lead.plan.later].map((i) => (
            <button key={i.sku} data-testid={`rec-${i.sku}`} onClick={() => toggle("recommended_product_lines", i.name)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${(form.recommended_product_lines || []).includes(i.name) ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-300 hover:border-brand-indigo/60"}`}>{i.name}</button>
          ))}
        </div>
        <Textarea data-testid="discovery-recommendation_why" rows={2} className={taCls + " mt-3"} placeholder="Why these, in this order" value={form.recommendation_why || ""} onChange={(e) => set("recommendation_why", e.target.value)} />
      </div>

      <div className="mt-7 grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-sm font-semibold text-zinc-200">Next action</p>
          <Input data-testid="discovery-next_action" className={inputCls + " mt-2"} value={form.next_action || ""} onChange={(e) => set("next_action", e.target.value)} />
        </div>
        <div>
          <p className="text-sm font-semibold text-zinc-200">Follow-up date</p>
          <Input data-testid="discovery-follow_up_date" type="date" className={inputCls + " mt-2"} value={form.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} />
        </div>
      </div>

      <Button data-testid="save-discovery-btn" disabled={busy} onClick={save}
        className="mt-8 h-11 rounded-full bg-brand-pink px-7 font-semibold text-zinc-950 hover:bg-brand-pink-soft transition-colors duration-200">Save discovery call</Button>
    </div>
  );
};
